import { serverTimestamp, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { pickMatchPlayerRefsFromLive } from "../../domain/identity/matchPlayerRefs";

const PLACEHOLDER_PLAYER = "unassigned";

function matchRef(matchId) {
  return doc(db, "matches", matchId);
}

export function mapLiveStatusToMatchStatus(liveStatus) {
  switch (liveStatus) {
    case "live":
      return "live";
    case "finished":
      return "finished";
    case "cancelled":
      return "cancelled";
    default:
      return "pending";
  }
}

/**
 * Legacy helper: maps live teams to match `player1Id` / `player2Id` (+ optional legacy names).
 * Prefers global `playerId` on roster rows; falls back to team title strings.
 */
export function pickPlayerIds(livePayload) {
  const refs = pickMatchPlayerRefsFromLive(livePayload);
  return {
    player1Id: refs.player1Id || PLACEHOLDER_PLAYER,
    player2Id: refs.player2Id || PLACEHOLDER_PLAYER,
    ...(refs.player1Name ? { player1Name: refs.player1Name } : {}),
    ...(refs.player2Name ? { player2Name: refs.player2Name } : {}),
  };
}

export function buildLiveMatchDocument(
  roomCode,
  managingUid,
  livePayload,
  existing,
  roomMatchId = null
) {
  const timestamps = { createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  const rmId = roomMatchId ? String(roomMatchId).trim() : "";
  const base = {
    matchId: roomCode,
    ownerId: managingUid,
    createdAt: existing?.createdAt ?? timestamps.createdAt,
    updatedAt: timestamps.updatedAt,
  };
  if (rmId) base.roomMatchId = rmId;

  const optional = {
    status: livePayload?.status,
    scoreA: livePayload?.scoreA != null ? Math.trunc(livePayload.scoreA) : undefined,
    scoreB: livePayload?.scoreB != null ? Math.trunc(livePayload.scoreB) : undefined,
    sets: livePayload?.sets,
    serving: livePayload?.serving,
    endsSwapped: livePayload?.endsSwapped,
    teamA: livePayload?.teamA,
    teamB: livePayload?.teamB,
    winner: livePayload?.winner,
    setsToWin: livePayload?.setsToWin != null ? Math.trunc(livePayload.setsToWin) : undefined,
    pointsToWin: livePayload?.pointsToWin != null ? Math.trunc(livePayload.pointsToWin) : undefined,
    matchTimerBaseMs:
      livePayload?.matchTimerBaseMs != null ? Math.trunc(livePayload.matchTimerBaseMs) : undefined,
    matchTimerRunSince: livePayload?.matchTimerRunSince ?? null,
    scoringMatch: livePayload?.scoringMatch,
  };

  const out = { ...base };
  Object.entries(optional).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value;
  });
  return out;
}

export function matchMetaToLegacyShape(matchDoc) {
  if (!matchDoc) return null;
  return {
    name: matchDoc.displayName || matchDoc.title || matchDoc.id,
    ownerUid: matchDoc.createdBy,
    createdAt: matchDoc.createdAt?.toMillis?.() ?? matchDoc.createdAt,
    hasViewCode: Boolean(matchDoc.hasViewCode),
    archived: Boolean(matchDoc.archivedAt),
  };
}

export async function patchMatchFields(matchId, patch) {
  await updateDoc(matchRef(matchId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}
